/**
 * Modal / Dialog — Radix Dialog wrapped with editorial styling.
 * - Backdrop: bone background with subtle blur, NOT black.
 * - Container: hairline border, generous padding, NO shadow.
 * - Motion: slow fade-in per UX motion tokens.
 */
import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import { cn } from '../../lib/cn';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Serif h3 title shown at the top of the modal. */
  title: string;
  description?: string;
  /** When true, the title is rendered visually but kept narrow. Defaults true. */
  showTitle?: boolean;
  children: React.ReactNode;
  /** Optional explicit max-width class (e.g., "max-w-lg"). */
  size?: 'sm' | 'md' | 'lg';
  /** Hide the X close button (e.g., for forced confirms). */
  hideCloseButton?: boolean;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  showTitle = true,
  children,
  size = 'md',
  hideCloseButton = false,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50',
            // Bone background w/ blur — NOT black. UX backdrop spec.
            'bg-bg/70 backdrop-blur-md',
            'data-[state=open]:animate-[fade-in_240ms_var(--ease-out-slow)]',
            'data-[state=closed]:animate-[fade-out_240ms_var(--ease-out-slow)]',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-[10vh] z-50 -translate-x-1/2',
            'w-[calc(100%-2rem)]',
            SIZES[size],
            'bg-bg border border-ink-200',
            'p-6 md:p-8',
            'focus:outline-none',
            'data-[state=open]:animate-[fade-in_280ms_var(--ease-out-slow)]',
          )}
        >
          <div className={cn(!showTitle && 'sr-only')}>
            <Dialog.Title className="font-serif text-2xl text-ink-900 leading-tight">
              {title}
            </Dialog.Title>
            {description ? (
              <Dialog.Description className="mt-2 text-sm text-ink-500">
                {description}
              </Dialog.Description>
            ) : null}
          </div>
          <div className={cn(showTitle && 'mt-6')}>{children}</div>
          {!hideCloseButton ? (
            <Dialog.Close
              className={cn(
                'absolute top-4 right-4 p-1',
                'text-ink-500 hover:text-ink-900',
                'transition-colors duration-200 ease-out-slow',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
              )}
              aria-label="Close"
            >
              <X size={20} weight="regular" />
            </Dialog.Close>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
